import * as React from 'react'
import { useState, useEffect } from 'react'
import i18n from 'i18n'
import { useTranslation } from 'react-i18next'
import {
    IconButtonSeeker,
    useToaster,
    GridSeeker,
    SelectSeeker,
    ModalSeeker,
    IconSeekers,
    SubmitButtonSeeker,
    SwitchSeeker,
    DateRangePickerSeeker
} from 'ui-seeker/core'
import { postSubmit, formatDate, FORMAT_DATE_BACK } from 'ui-seeker/utils'
import CreatableSelect from 'react-select/creatable'
import FormHelperText from "@material-ui/core/FormHelperText"

import { validateEmail } from 'app/utils'
import { useFilterSection } from '../hooks'
import { useCommonFilters } from 'app/components'
import { getAppUserData } from "app/@utils";
import { AppTooltip } from 'app/@components'
import { COMPETITION_URL, PARITY_URL, PACKAGES_URL } from 'app/@common/constants'
import { useContextSelector } from 'use-context-selector'
import { CompetitionAnalyzerVsPreviousPriceContext } from 'app/pages/Competition/Analyzer2.1/Tables/Providers'
import { PackagesAnalyzerVsPreviousPriceContext } from 'app/pages/Packages/Analyzer2.1/Content/Tables/Providers'


const DATE_RANGE_URLS = {
    competition: COMPETITION_URL.DATE_RANGE,
    parity: PARITY_URL.DATE_RANGE,
    packages: PACKAGES_URL.DATE_RANGE,
}

const capitalize = s => s ? s.charAt(0).toUpperCase() + s.slice(1) : s

// Backend sends value='YYYYMM' + label='June 2026' (EN). Localize the month name by the
// numeric value via Intl using the active i18n language; fall back to the EN label.
// Same approach as commonFilters/components/select/SelectMonth.jsx.
const formatMonthOption = option => {
    const { value, label } = option
    if (typeof value === 'string' && /^\d{6}$/.test(value)) {
        const year = value.slice(0, 4)
        const monthIdx = parseInt(value.slice(4, 6), 10) - 1
        const monthName = capitalize(new Intl.DateTimeFormat(i18n.language, { month: 'long' }).format(new Date(Number(year), monthIdx, 1)))
        return `${monthName} ${year}`
    }
    return label
}


export default function ExcelBtn() {

    const { t } = useTranslation('common')
    const section = useFilterSection()
    const createOption = label => ({
        label,
        value: label,
    })

    const { email, priceByNight } = getAppUserData()
    // localStorage.email puede venir null o como string "null" → no crear un chip vacío.
    const initialEmails = email && email !== "null" ? [createOption(email)] : []
    const [loading, setLoading] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [emails, setEmails] = useState(initialEmails)
    const [emailsOK, setEmailsOK] = useState(true)
    const [submit, setSubmit] = useState(false)
    const [inputValue, setInputValue] = useState('')
    const [monthOptions, setMonthOptions] = useState([])
    const [selectedMonths, setSelectedMonths] = useState([])
    const [useCustomRange, setUseCustomRange] = useState(false)
    const [customRange, setCustomRange] = useState([null, null])
    const [maxDate, setMaxDate] = useState(null)
    const [loadingOptions, setLoadingOptions] = useState(false)
    const { showToaster } = useToaster()
    const { dataFilters, dateRange, customDateRange, mainHotels } = useCommonFilters()
    // Toggle "vs Previous Price" → propagamos el chip activo al export. Selectores
    // defensivos: si el componente se monta fuera del provider (p. ej. en Parity), el
    // valor del contexto es el default `{}` y `selected` queda como null.
    const competitionVsSelected = useContextSelector(
        CompetitionAnalyzerVsPreviousPriceContext,
        value => value?.state?.selected || null
    )
    const packagesVsSelected = useContextSelector(
        PackagesAnalyzerVsPreviousPriceContext,
        value => value?.state?.selected || null
    )

    // ≥3 meses = export grande: email OBLIGATORIO y envío por mail forzado.
    const isBigExport = selectedMonths.length >= 3
    // El campo de email se muestra también en custom range, pero ahí es opcional.
    const showEmailField = isBigExport || useCustomRange
    const hasEmails = emails.some(e => e.value && e.value !== "null")
    // Se envía por mail (async) si es export grande, o si en custom range el user puso email.
    // Custom range sin email → descarga directa (sync).
    const sendByMail = isBigExport || (useCustomRange && hasEmails)

    const handleCloseModal = () => {
        setOpenModal(false)
        setSelectedMonths([])
        setUseCustomRange(false)
        setCustomRange([null, null])
        setEmails(initialEmails)
        setEmailsOK(true)
        setInputValue('')
    }

	const downloadFile = fileUrl => {
		const link = document.createElement('a')
		link.href = fileUrl
		link.download = fileUrl
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
	}

    useEffect(() => {
        if (!openModal) return

        const url = DATE_RANGE_URLS[section]
        if (!url) return

        setLoadingOptions(true)
        postSubmit(url, { mainHotels }).then(options => {
            const customOption = options.find(opt => opt.value === 'custom')
            const months = options.filter(opt => opt.value !== 'custom')

            setMonthOptions(months)
            if (customOption?.lastDate) setMaxDate(new Date(customOption.lastDate))

            if (dateRange === 'custom') {
                setUseCustomRange(true)
                setSelectedMonths([])
                if (customDateRange?.[0] && customDateRange?.[1]) {
                    setCustomRange([new Date(customDateRange[0]), new Date(customDateRange[1])])
                }
            } else {
                const currentMonth = months.find(m => m.value === dateRange)
                setSelectedMonths(currentMonth ? [currentMonth] : months.length ? [months[0]] : [])
            }
        }).catch(() => {
            showToaster.error(t('excel.errorLoadingDates'))
        }).finally(() => {
            setLoadingOptions(false)
        })
    }, [openModal])

    const buildDateParams = () => {
        if (useCustomRange) {
            return {
                dateRange: 'custom',
                startDate: formatDate(customRange[0], { format: FORMAT_DATE_BACK }),
                endDate: formatDate(customRange[1], { format: FORMAT_DATE_BACK }),
            }
        }
        if (selectedMonths.length === 1) {
            return { dateRange: selectedMonths[0].value }
        }
        return { dateRange: selectedMonths.map(m => m.value) }
    }

    const getData = () => {
        setLoading(true)
        showToaster.info(t('excel.pleaseWait'))

        const urlSection = section
        const url = `/api/${urlSection}/export/excel/async`
        // Sheet "vs Last N Days" parametrizable: si el toggle del dashboard activo tiene
        // un chip seleccionado, lo enviamos como diff_days. Si está apagado, omitimos el
        // campo y el backend salta la sheet (decisión C-bis del plan).
        const vsSelectedTarget = urlSection === 'competition' ? competitionVsSelected?.target
            : urlSection === 'packages' ? packagesVsSelected?.target
            : null

        postSubmit(url, {
            ...dataFilters,
            ...buildDateParams(),
            ...(vsSelectedTarget && { diff_days: vsSelectedTarget }),
            ...(sendByMail && hasEmails) && { session_data: { user_email: emails.filter(e => e.value && e.value !== "null").map(a => a.value), async: true } },
            nightly: (/true/).test(priceByNight)
        }).then(res => {
            if (sendByMail) {
                showToaster.success(t('excel.exportingByEmail'))
            } else {
                showToaster.success(t('excel.downloading'))
                downloadFile(res.storage_url)
            }
        }).catch(e => {
            showToaster.error(e.status === 400 ? t('excel.errorMail') : t('excel.errorProcessing'))
        }).finally(() => {
            handleCloseModal()
            setLoading(false)
            setSubmit(false)
        })
    }

    useEffect(() => {
        if (!submit) return
        // Si el usuario escribió emails, validarlos siempre (big export o custom range opcional).
        if (emails.length) {
            const hasInvalid = emails.some(a => !validateEmail(a.value))
            if (hasInvalid) {
                setSubmit(false)
                setEmailsOK(false)
                return
            }
        } else if (isBigExport) {
            // Export grande (≥3 meses): email obligatorio.
            setSubmit(false)
            setEmailsOK(false)
            return
        }
        setEmailsOK(true)
        getData()
    }, [submit])

    const handleKeyDown = event => {
        if (!inputValue) return;

        switch (event.key) {
            case 'Enter':
            case 'Tab':
                setEmails(prev => [...prev, createOption(inputValue)])
                setInputValue('')
                event.preventDefault()
                break;
            default:
                break;
        }

        if (event.type === 'blur') {
            setEmails(prev => [...prev, createOption(inputValue)])
            setInputValue('')
            event.preventDefault()
        }
    }

    const modalConfig = {
        isOpen: openModal,
        controller: handleCloseModal,
        title: t('excel.downloadExcel'),
        okBtn: false,
        cancelBtn: false
    }

    const configTagInput = {
        components: { DropdownIndicator: null }, inputValue: inputValue, isClearable: true, isMulti: true,
        menuIsOpen: false, placeholder: t('excel.insertEmails'), value: emails,
        onChange: newValue => setEmails(newValue), 
        onInputChange: newValue => setInputValue(newValue),
        onKeyDown: handleKeyDown, onMouseOver: handleKeyDown, onBlur: handleKeyDown, 
    }

    return <>
        <AppTooltip title={t('excel.exportToExcel')}>
            <IconButtonSeeker
                icon="file-excel"
                iconType="fontAwesome"
                className="icon-filter-excel-custom"
                onClick={() => setOpenModal(true)}
            />
        </AppTooltip>

        <ModalSeeker {...modalConfig}>
            <GridSeeker item xs={12}>
                <SelectSeeker
                    isMulti
                    label={t('excel.selectMonths')}
                    options={monthOptions}
                    value={selectedMonths.map(m => m.value)}
                    onChange={setSelectedMonths}
                    disabled={useCustomRange}
                    isLoading={loadingOptions}
                    closeMenuOnSelect={false}
                    formatOptionLabel={formatMonthOption}
                />
            </GridSeeker>
            <GridSeeker item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
                    <SwitchSeeker
                        checked={useCustomRange}
                        onChange={e => {
                            const checked = e.target.checked
                            setUseCustomRange(checked)
                            if (checked) setSelectedMonths([])
                        }}
                    />
                    <span style={{ marginLeft: '-15px' }}>{t('excel.customDateRange')}</span>
                </div>
            </GridSeeker>
            {useCustomRange && <GridSeeker item xs={12}>
                <DateRangePickerSeeker
                    value={customRange[0] && customRange[1] ? customRange : []}
                    onChange={range => setCustomRange(range || [null, null])}
                    disabledDate={maxDate ? d => d > maxDate : null}
                    label={t('excel.dateRange')}
                    cleanable
                />
            </GridSeeker>}
            {showEmailField && <GridSeeker item xs={12}>
                <fieldset className="selectSeeker-wrapper" style={{ borderRadius: '4px', marginTop: 8 }}>
                    <legend>{t('excel.emails')}</legend>
                    <label>{t('excel.emails')} {isBigExport ? '*' : ''}</label>
                    <div className="MuiInputBase-root MuiInputBase-fullWidth MuiInputBase-formControl MuiInputBase-adornedStart MuiOutlinedInput-adornedStart container-creatable">
                        <IconSeekers icon='envelope' iconType='fontAwesome' iconWeight='fal' />
                        <CreatableSelect {...configTagInput} />
                    </div>
                </fieldset>
                <span style={{ fontSize: '10px', color: 'lightgrey', marginTop: 4 }}>{isBigExport
                    ? t('excel.bigExportNote')
                    : t('excel.emailOptionalNote')}</span>
                {!emailsOK && <FormHelperText style={{ color: 'red' }}>{t('excel.insertEmailsError')}</FormHelperText>}
            </GridSeeker>}
            <GridSeeker className="flex flex-end modal-excel-footer" size={12}>
                <SubmitButtonSeeker className={`modal-btn bg-secondary`} onClick={() => {
                    if (useCustomRange && (!customRange[0] || !customRange[1])) return
                    if (!useCustomRange && selectedMonths.length === 0) return
                    setSubmit(true)
                }} isLoading={loading} style={{ margin: '10px 0 0 0' }}>
                    {t('excel.ok')}
                </SubmitButtonSeeker>
            </GridSeeker>
        </ModalSeeker>
    </>
}